import { useState, useEffect, useLayoutEffect } from 'react';
import Navigation from './components/Navigation';
import HomePage from './components/HomePage';
import AboutPage from './components/AboutPage';
import InfinityPage from './components/InfinityPage';
import WritingsPage from './components/WritingsPage';
import MediaPage from './components/MediaPage';
import ContactPage from './components/ContactPage';
import ThesisPage from './components/ThesisPage';
import SubtleOrbs from './components/SubtleOrbs';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';
import { usePageSectionFadeIn } from './components/FadeIn';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [previousPage, setPreviousPage] = useState(null);
  const [language, setLanguage] = useState('en');

  // Initialize page based on URL
  useEffect(() => {
    const path = window.location.pathname;
    const pageMap = {
      '/': 'home',
      '/home': 'home',
      '/about': 'about',
      '/infinity': 'infinity',
      '/writings': 'writings',
      '/media': 'media',
      '/contact': 'contact',
      '/thesis': 'thesis'
    };

    const page = pageMap[path] || 'home';
    setCurrentPage(page);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const pageMap = {
        '/': 'home',
        '/home': 'home',
        '/about': 'about',
        '/infinity': 'infinity',
        '/writings': 'writings',
        '/media': 'media',
        '/contact': 'contact',
        '/thesis': 'thesis'
      };

      const page = pageMap[path] || 'home';
      setCurrentPage(page);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;

    root.style.scrollBehavior = 'auto';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    root.style.scrollBehavior = previousScrollBehavior;
  }, [currentPage]);

  usePageSectionFadeIn(currentPage, language);

  // Enhanced setCurrentPage that also updates URL and tracks previous page
  const handlePageChange = (page) => {
    setPreviousPage(currentPage);
    setCurrentPage(page);
    const pathMap = {
      'home': '/',
      'about': '/about',
      'infinity': '/infinity',
      'writings': '/writings',
      'media': '/media',
      'contact': '/contact',
      'thesis': '/thesis'
    };

    const path = pathMap[page] || '/';
    window.history.pushState({ page }, '', path);
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'home': return <HomePage language={language} setCurrentPage={handlePageChange} />;
      case 'about': return <AboutPage language={language} />;
      case 'infinity': return <InfinityPage />;
      case 'writings': return <WritingsPage language={language} setCurrentPage={handlePageChange} />;
      case 'media': return <MediaPage language={language} />;
      case 'contact': return <ContactPage language={language} />;
      case 'thesis': return <ThesisPage previousPage={previousPage} setCurrentPage={handlePageChange} language={language} />;
      default: return <HomePage language={language} />;
    }
  };

  const fromPage = previousPage || 'writings';
  const pageLabels = { home: 'Home', writings: 'Writings', about: 'About', infinity: 'Infinity', media: 'Media', contact: 'Contact' };
  const fromLabel = pageLabels[fromPage] || 'Back';

  return (
    <div className="min-h-screen bg-white">
      <SubtleOrbs />
      <Navigation
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        language={language}
        setLanguage={setLanguage}
      />

      {/* Back buttons — rendered outside page-enter to avoid transform stacking context trapping fixed position */}
      {currentPage === 'thesis' && (
        <>
          {/* Desktop */}
          <button
            onClick={() => handlePageChange(fromPage)}
            aria-label={`Back to ${fromLabel}`}
            className="back-btn-desktop"
            style={{
              position: 'fixed',
              left: '24px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 40,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '1px solid rgba(200,169,110,0.35)',
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B6862',
              transition: 'border-color 0.2s, color 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#C8A96E';
              e.currentTarget.style.color = '#C8A96E';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(200,169,110,0.18)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(200,169,110,0.35)';
              e.currentTarget.style.color = '#6B6862';
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {/* Mobile */}
          <button
            onClick={() => handlePageChange(fromPage)}
            aria-label={`Back to ${fromLabel}`}
            className="back-btn-mobile"
            style={{
              position: 'fixed',
              left: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 40,
              width: '16px',
              height: '40px',
              borderRadius: '0',
              border: '1px solid rgba(200,169,110,0.35)',
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B6862',
              transition: 'border-color 0.2s, color 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#C8A96E';
              e.currentTarget.style.color = '#C8A96E';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(200,169,110,0.18)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(200,169,110,0.35)';
              e.currentTarget.style.color = '#6B6862';
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </>
      )}

      <main className="relative z-10">
        <div key={`${currentPage}-${language}`} className="page-enter">
          {renderCurrentPage()}
        </div>
      </main>

      <Footer
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        language={language}
      />
      <BackToTop />
    </div>
  );
};

export default App;
